import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@shared/lib/api";
import type { UnreadCountResponse } from "@shared/lib/types";

const POLL_INTERVAL = 120000; // 120 seconds

type UseMessagingPollingResult = {
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
};

/**
 * Hook for polling messaging unread count.
 * Polls every 120 seconds and provides a manual refresh function.
 *
 * @param enabled - Whether polling should be active (default: true)
 * @returns Object with unreadCount, loading state, error, and refresh function
 */
export function useMessagingPolling(enabled = true): UseMessagingPollingResult {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const res = await apiFetch<UnreadCountResponse>("/api/v1/messages/unread_count");
      setUnreadCount(res.data.unread_count);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch unread count");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  // Initial fetch and polling
  useEffect(() => {
    if (!enabled) return;

    // Fetch immediately
    fetchUnreadCount();

    // Set up polling interval
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [enabled, fetchUnreadCount]);

  const refresh = useCallback(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  return {
    unreadCount,
    loading,
    error,
    refresh,
  };
}
